<template>
  <LayoutPortal
    :isLoading="eventApi.loading && eventApi.status !== 'error'"
    :isContentReady="!!compEvent"
    :isMissing="!eventApi.loading && !compEvent"
    :bannerFileRef="(compEvent && compEvent.bannerFileRef) || undefined"
    :logoFileRef="(compEvent && compEvent.avatarFileRef) || undefined"
  >
    <template #title v-if="compEvent">
      <h1 class="text-3xl z-3">{{ compEvent.name }}</h1>

      <p>
        <FontAwesomeIcon :icon="faCalendarAlt" class="mr-1" />
        {{ eventDateDisplay(compEvent) }}
      </p>
    </template>

    <template #subHeader v-if="compShowPlainHeader || compIsFinished">
      <div v-if="compShowPlainHeader" class="text-center md:text-left mt-2 px-5 xl:px-0">
        <h1 class="text-3xl">{{ compEvent.name }}</h1>

        <p class="mt-2">
          <FontAwesomeIcon :icon="faCalendarAlt" class="mr-1" />
          {{ eventDateDisplay(compEvent) }}
        </p>
      </div>

      <div v-if="compIsFinished" class="px-5 xl:px-0" :class="compShowPlainHeader ? 'mt-4' : 'mt-12'">
        <UtilMessage type="information">
          This event has now finished.
          <UtilTextLink to="/events">View all upcoming events</UtilTextLink>
        </UtilMessage>
      </div>
    </template>

    <template #sidebar v-if="compEvent">
      <div v-if="compHasSidebarContent" class="container-padding">
        <template v-if="compEvent.description">
          <div class="whitespace-pre-line">
            <p v-if="compEvent.description.length < 300 || isDescriptionExpanded" class="-mt-5">
              {{ compEvent.description.trim() }}
            </p>

            <div v-else>
              <p>{{ compEvent.description.trim().slice(0, 300) }}...</p>

              <UtilTextLink
                @click="isDescriptionExpanded = true"
                class="text-brand mt-2 no-underline flex items-start"
              >
                <span class="mr-2">Read more</span>
                <FontAwesomeIcon :icon="faSortDown" />
              </UtilTextLink>
            </div>
          </div>

          <UtilDivider v-if="compCanTakeAction" class="mt-5 mb-5" />
        </template>

        <UtilButton
          v-if="compCanRegister && !compCanViewBidding"
          @click="register()"
          color="primary"
          class="w-full"
        >
          <FontAwesomeIcon :icon="faClipboardListCheck" />
          <span>Register For Event</span>
        </UtilButton>

        <UtilButton v-if="compCanViewCatalogue" @click="viewEventCatalogue()" color="primary" class="w-full">
          <FontAwesomeIcon :icon="faBookOpen" />
          <span>View Catalogue</span>
        </UtilButton>

        <UtilButton v-if="compCanViewBidding" @click="viewBidding()" color="primary" class="w-full">
          <FontAwesomeIcon :icon="faGavel" />
          <span>View Online Bidding</span>
        </UtilButton>
      </div>
    </template>

    <template #sidebarSecondary v-if="compEvent && $breakpoint.mdAndUp">
      <LayoutPanel title="Upcoming Events" :class="{ 'mt-8': compHasSidebarContent }">
        <UtilEventsUpcoming
          linkPadding="pl-2 pr-3"
          :isLoading="upcomingEventsApi.loading"
          :events="upcomingEventsApi.data"
          :limit="3"
        />
      </LayoutPanel>
    </template>

    <template #mainContent>
      <template v-if="eventApi.loading || compEvent.listings.length">
        <div class="flex flex-wrap">
          <div
            v-for="listing in compEvent.listings"
            :key="listing.listingRef"
            class="w-full sm:w-6/12 lg:w-4/12 px-2 mb-4"
          >
            <UtilHitCard type="listing" :listing="listing" :to="`/view/listing/${listing.url}`" />
          </div>
        </div>
      </template>

      <div v-else class="flex-auto relative flex-col">
        <UtilEmpty
          :icon="faFarm"
          title="No Preview Listings Yet"
          class="rounded py-6 px-4 mx-auto sticky"
          style="top: 20px;"
        >
          <template #message>
            <p>No preview listings added to this event yet</p>

            <UtilButton
              v-if="compEvent && compEvent.linkCatalogue"
              @click="viewEventCatalogue()"
              color="secondary"
              class="mt-4"
            >
              <FontAwesomeIcon :icon="faBookOpen" />
              View Catalogue
            </UtilButton>
          </template>
        </UtilEmpty>
      </div>
    </template>

    <template #footer v-if="$breakpoint.xs">
      <LayoutPanel title="Upcoming Auctions & Events">
        <UtilEventsUpcoming :isLoading="upcomingEventsApi.loading" :events="upcomingEventsApi.data" />
      </LayoutPanel>
    </template>
  </LayoutPortal>
</template>

<script>
import {
  faCalendarAlt,
  faFarm,
  faBookOpen,
  faGavel,
  faClipboardListCheck,
  faSortDown
} from '@fortawesome/pro-regular-svg-icons'
import LegacyApi from '@/lib/api/legacy'
import { get } from 'lodash'
import { generateMetaTags } from '@/lib/generate-meta'
import eventDateDisplay from '@/lib/event-date'
import generateFileRefOgImage from '@/lib/og-image-fileref'
import LayoutPortal from '@/components/layout/LayoutPortal'
import eventStatus from '@/lib/mapping/event-status'
import verificationRequired from '@/lib/mapping/verification-required'
import UtilEventsUpcoming from '@/components/utils/UtilEventsUpcoming'
import LayoutPanel from '@/components/layout/LayoutPanel'
import UtilDivider from '@/components/utils/UtilDivider'
import UtilHitCard from '@/components/utils/UtilHitCard'
import UtilEmpty from '@/components/utils/UtilEmpty'
import UtilButton from '@/components/utils/UtilButton'
import UtilTextLink from '@/components/utils/UtilTextLink'
import UtilMessage from '@/components/utils/UtilMessage'

export default {
  name: 'EventDetail',
  components: {
    LayoutPortal,
    UtilEventsUpcoming,
    LayoutPanel,
    UtilDivider,
    UtilHitCard,
    UtilEmpty,
    UtilButton,
    UtilTextLink,
    UtilMessage
  },
  data() {
    return {
      eventApi: new LegacyApi(),
      upcomingEventsApi: new LegacyApi(),
      isDescriptionExpanded: false,
      isAutoRedirect: false,
      eventDateDisplay,
      faCalendarAlt,
      faFarm,
      faBookOpen,
      faGavel,
      faClipboardListCheck,
      faSortDown
    }
  },
  watch: {
    '$route.path'() {
      this.getEvent()
    }
  },
  created() {
    this.getEvent()
    this.getUpcomingEvents()
  },
  computed: {
    compEvent() {
      return get(this.eventApi, 'data[0].name') ? this.eventApi.data[0] : null
    },

    compOgImage() {
      if (!this.compEvent || !this.compEvent.bannerFileRef) {
        return false
      }

      return generateFileRefOgImage(this.compEvent.bannerFileRef, this.compEvent.name)
    },

    compShowPlainHeader() {
      return (
        this.compEvent && (this.$breakpoint.xs || (!this.compEvent.bannerFileRef && !this.compEvent.avatarFileRef))
      )
    },

    compHasSidebarContent() {
      return (
        this.compEvent.description || this.compCanRegister || this.compCanViewCatalogue || this.compCanViewBidding
      )
    },

    compBreadcrumbs() {
      let breadcrumbs = [
        {
          label: 'All Events',
          to: '/events'
        }
      ]

      if (this.compEvent) {
        breadcrumbs.push({
          label: this.compEvent.name,
          current: true
        })
      }

      return breadcrumbs
    },

    compCanRegister() {
      if (!this.compEvent || !this.compEvent.linkRegister || this.compCanViewBidding) {
        return false
      }

      return this.$date().isBetween(
        this.$date(this.compEvent.registrationStartDate, this.$date.legacyFormat),
        this.$date(this.compEvent.registrationEndDate, this.$date.legacyFormat)
      )
    },

    // Allow user to view catalogue up to an hour before start, but only if there is no option to register or view bidding
    compCanViewCatalogue() {
      if (!this.compEvent || !this.compEvent.linkCatalogue || this.compCanRegister || this.compCanViewBidding) {
        return false
      }

      return this.$date().isBefore(this.$date(this.compEvent.startDate, this.$date.legacyFormat))
    },

    compCanViewBidding() {
      if (!this.compEvent || !this.compEvent.linkBidding) {
        return false
      }

      // No endDate, let's assume it's a 1 day event
      if (!this.compEvent.endDate) {
        return this.$date().isSame(this.$date(this.compEvent.startDate, this.$date.legacyFormat), 'day')
      }

      return this.$date().isBetween(
        this.$date(this.compEvent.startDate, this.$date.legacyFormat),
        this.$date(this.compEvent.endDate, this.$date.legacyFormat)
      )
    },

    compCanTakeAction() {
      return this.compCanViewBidding || this.compCanViewCatalogue || this.compCanRegister
    },

    compIsFinished() {
      if (!this.compEvent) {
        return false
      }

      return this.$date().isAfter(this.$date(this.compEvent.endDate, this.$date.legacyFormat))
    },

    compIsVerificationRequired() {
      return (
        this.compEvent &&
        this.compEvent.verificationRequired !== verificationRequired.NONE &&
        !this.$store.getters['securePayments/isVerified']
      )
    }
  },
  methods: {
    async getEvent() {
      await this.eventApi
        .get('events.get', {
          includeListings: 1,
          eventRef: this.$route.params.eventRef
        })
        .catch(() => {})

      await this.$nextTick()

      this.$emit('updateHead')

      if (this.compIsVerificationRequired) {
        this.$store.dispatch('securePayments/getProfile')
      }

      if (this.$route.query.redirect) {
        this.isAutoRedirect = true

        // Stop auto redirect triggering if pressing back in browser
        this.$router.replace(`/events/${this.$route.params.eventRef}`).catch(() => {})

        if (this.compCanViewBidding) {
          this.viewBidding()
        } else if (this.compCanRegister) {
          this.register()
        } else if (this.compCanViewCatalogue) {
          this.viewEventCatalogue()
        }
      }
    },

    async getUpcomingEvents() {
      this.upcomingEventsApi.get('events.get', {
        includeListings: 0,
        rangeFrom: this.$date().subtract(24, 'hour').format('DD-MM-YYYY'),
        rangeTo: this.$date().add(4, 'week').format('DD-MM-YYYY'),
        statuses: [eventStatus.LIVE, eventStatus.FINISHED, eventStatus.PENDING]
      })
    },

    register() {
      this.eventCTARedirect('register', this.compEvent.linkRegister)
    },

    viewEventCatalogue() {
      this.eventCTARedirect('catalogue', this.compEvent.linkCatalogue)
    },

    viewBidding() {
      this.eventCTARedirect('bidding', this.compEvent.linkBidding)
    },

    eventCTARedirect(type, url) {
      this.$analytics.addEvent({
        category: 'events',
        action: `Clicked view ${type}`,
        label: this.compEvent.name
      })

      // Popups will be blocked on auto redirect so we need another manual click from the user :(
      if (this.compIsVerificationRequired || !this.appIs.auth || this.isAutoRedirect) {
        this.$modal.open('ModalEventAuthRedirect', {
          redirectUrl: url,
          requireFarmPayVerification: this.compIsVerificationRequired,
          forceReady: this.isAutoRedirect && !this.compIsVerificationRequired && this.appIs.auth,
          eventName: this.compEvent.name
        })
      } else {
        this.$openWindow(url, true)
      }
    }
  },
  head: {
    title() {
      return {
        inner: this.compEvent ? this.compEvent.name : 'Event'
      }
    },
    meta() {
      return generateMetaTags({
        title: this.compEvent ? this.compEvent.title : 'Event',
        description: this.compEvent ? this.compEvent.description.substring(0, 150) : undefined,
        image: this.compOgImage
      })
    }
  }
}
</script>
